import type { BlastTripMetric, EmailBlastSendingDomainPlanDto } from '@/types/emailBlast'
import { sendingDomainPill, type SendingDomainRateLimitsDto, type SendingDomainState } from '@/types/sendingDomains'
import { domainStopCause, type DomainStopCause } from '@/utils/sendingDomainStatus'
import { formatCount, formatLimit, localTimeWithZone } from '@/utils/sendingDomainWarmup'
import { parseServerDate, parseServerTimestamp } from '@/utils/time'

// How a broadcast's sending domain governs it (ORB-953), in the broadcast page's words. The API works out
// every figure (today's share, the days ahead, a stopped domain's resume); this only lays them out and
// restates no warm-up rule.

const DAY_MS = 24 * 60 * 60 * 1000

// Fixed rather than Intl, like the short forms in sendingDomainWarmup, so a day reads the same in every browser.
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** The broadcast's domain state, or null with no domain or one this build does not know (sendingDomainPill logs it). */
export function planDomainState(plan: EmailBlastSendingDomainPlanDto | null): SendingDomainState | null {
  if (!plan?.Domain) return null
  const pill = sendingDomainPill(plan.Domain)
  return pill === 'Holding' ? 'Warming' : pill
}

/** One UTC day of the broadcast: what it sent, and what the forecast expects on top. */
export interface BroadcastPlanDay {
  dayStartUtc: string
  startMs: number
  sent: number
  expected: number
  isToday: boolean
  /** The step's hourly limit that day; null on steps 1 to 3 and once the domain is expected to be Ready. */
  hourlyLimit: number | null
  /** The domain is expected to be Ready that day, with no limit. */
  ready: boolean
  /** The event start, on the day it falls. */
  endsAtUtc: string | null
  /** Set on the first later day with sends expected: when they start. */
  startsAtUtc: string | null
}

/**
 * The days sent so far and the days the forecast expects, merged by UTC day, oldest first. Empty unless the
 * API forecast the broadcast, which it does only while a warming domain paces a broadcast with emails waiting.
 */
export function buildPlanDays(plan: EmailBlastSendingDomainPlanDto): BroadcastPlanDay[] {
  if (!plan.Forecast) return []

  const todayMs = parseServerTimestamp(plan.UsageDateUtc)
  const byDay = new Map<number, BroadcastPlanDay>()
  const dayAt = (dayStartUtc: string): BroadcastPlanDay | null => {
    const startMs = parseServerTimestamp(dayStartUtc)
    if (startMs === null) {
      console.error('Unparseable broadcast plan day', dayStartUtc)
      return null
    }
    const known = byDay.get(startMs)
    if (known) return known
    const day: BroadcastPlanDay = {
      dayStartUtc,
      startMs,
      sent: 0,
      expected: 0,
      isToday: startMs === todayMs,
      hourlyLimit: null,
      ready: false,
      endsAtUtc: null,
      startsAtUtc: null,
    }
    byDay.set(startMs, day)
    return day
  }

  for (const entry of plan.History) {
    const day = dayAt(entry.DayStartUtc)
    if (day) day.sent = entry.Sent
  }
  for (const entry of plan.Forecast.Days) {
    const day = dayAt(entry.DayStartUtc)
    if (!day) continue
    day.expected = entry.Expected
    day.hourlyLimit = entry.HourlyLimit ?? null
    day.ready = entry.Step == null
    day.endsAtUtc = entry.EndsAtUtc ?? null
  }
  // A broadcast that has started keeps today on its plan even when it has sent nothing yet today, so the
  // days read in order and the day-total line has a day to belong to.
  if (plan.History.length > 0 && plan.UsageDateUtc) dayAt(plan.UsageDateUtc)

  const days = [...byDay.values()].sort((a, b) => a.startMs - b.startMs)
  const next = todayMs === null ? undefined : days.find(day => day.startMs > todayMs && day.expected > 0)
  if (next) next.startsAtUtc = nextStartUtc(plan.NextBatchAtUtc, next)
  return days
}

// A later day's sends start when the broadcast's next email is due. An email held for a day keeps its time of
// day, so a next batch dated before that day, a row not yet moved into it, still says what time it starts.
function nextStartUtc(nextBatchAtUtc: string | null | undefined, day: BroadcastPlanDay): string | null {
  const nextBatchMs = parseServerTimestamp(nextBatchAtUtc)
  if (!nextBatchAtUtc || nextBatchMs === null) return null
  if (nextBatchMs >= day.startMs && nextBatchMs < day.startMs + DAY_MS) return nextBatchAtUtc

  return new Date(day.startMs + (nextBatchMs % DAY_MS)).toISOString()
}

/** Where each day ends on the progress bar, as a percent, for every day that ends short of the whole list. */
export function dayEndPercents(days: BroadcastPlanDay[], recipients: number): { percent: number; dayStartUtc: string }[] {
  if (recipients <= 0) return []
  const ends: { percent: number; dayStartUtc: string }[] = []
  let covered = 0
  for (const day of days) {
    const share = day.sent + day.expected
    covered += share
    if (share > 0 && covered < recipients) ends.push({ percent: (covered / recipients) * 100, dayStartUtc: day.dayStartUtc })
  }
  return ends
}

/**
 * ", and today's limit is used up, so the rest are waiting for tomorrow's allowance." — what follows
 * "1,640 of 7,380 sent" in the multi-day verdict.
 */
export function todayClause(plan: EmailBlastSendingDomainPlanDto, days: BroadcastPlanDay[]): string {
  if (plan.IsWaitingForTomorrow) return ", and today's limit is used up, so the rest are waiting for tomorrow's allowance."
  const today = days.find(day => day.isToday && day.expected > 0)
  if (!today) return '.'
  const pace = today.hourlyLimit ? `, up to ${formatCount(today.hourlyLimit)} an hour` : ''
  return `; about ${formatCount(today.expected)} more are expected today${pace}.`
}

/** "the next 1,500 start Tuesday at 02:00 CEST", in the viewer's own time, once today has nothing more to send. */
export function nextBatchClause(days: BroadcastPlanDay[]): string | null {
  const upcoming = days.find(day => day.expected > 0)
  if (!upcoming?.startsAtUtc) return null
  return `the next ${formatCount(upcoming.expected)} start ${localWeekday(upcoming.startsAtUtc)} at ${localTimeWithZone(upcoming.startsAtUtc)}`
}

/** "expected to finish Thursday 1 October, a week before the event", or how many would miss the event. */
export function finishClause(plan: EmailBlastSendingDomainPlanDto): string | null {
  const forecast = plan.Forecast
  if (!forecast) return null
  if (forecast.AfterDeadline > 0) {
    return `at this pace ${formatCount(forecast.AfterDeadline)} won't go before the event starts, so they won't be sent`
  }
  if (!forecast.FinishDayUtc) {
    console.error('Broadcast forecast has no finish day and nothing after the event', plan.BlastId)
    return null
  }
  const finishMs = parseServerTimestamp(forecast.FinishDayUtc)
  const when = finishMs !== null && finishMs === parseServerTimestamp(plan.UsageDateUtc) ? 'today' : utcLongDate(forecast.FinishDayUtc)
  const margin = forecast.EventStartUtc ? beforeEvent(forecast.FinishDayUtc, forecast.EventStartUtc) : null
  return `expected to finish ${when}${margin ? `, ${margin}` : ''}`
}

/** A clause as its own sentence. */
export function asSentence(clause: string): string {
  return `${clause.charAt(0).toUpperCase()}${clause.slice(1)}.`
}

// Whole UTC days from the finish day to the event's day: "a week before the event".
function beforeEvent(finishDayUtc: string, eventStartUtc: string): string | null {
  const finishMs = parseServerTimestamp(finishDayUtc)
  const eventMs = parseServerTimestamp(eventStartUtc)
  if (finishMs === null || eventMs === null) {
    console.error('Unparseable forecast dates', finishDayUtc, eventStartUtc)
    return null
  }
  const days = Math.round((Math.floor(eventMs / DAY_MS) * DAY_MS - finishMs) / DAY_MS)
  if (days <= 0) return 'the day the event starts'
  if (days === 1) return 'the day before the event'
  if (days % 7 === 0) return days === 7 ? 'a week before the event' : `${days / 7} weeks before the event`
  return `${days} days before the event`
}

/** A run of a sentence; `figure` runs carry the numbers a reader scans for. */
export interface Phrase {
  text: string
  figure?: boolean
}

/**
 * "events.ortec.com has used 1,000 of 1,000 today: 940 by this broadcast, 60 by other emails." The day's
 * count cannot tell registrant emails from other broadcasts, so everything else is "other emails".
 */
export function domainDayTotal(plan: EmailBlastSendingDomainPlanDto): Phrase[] | null {
  const domain = plan.Domain
  const total = plan.DomainSentToday
  const broadcast = plan.BroadcastSentToday
  const other = plan.OtherEmailsSentToday
  if (!domain || domain.DailyLimit == null || total == null || broadcast == null || other == null) return null

  const limit = formatCount(domain.DailyLimit)
  if (total === 0) return [{ text: `${domain.Domain} has used none of its ` }, { text: limit, figure: true }, { text: ' today.' }]

  const used: Phrase[] = [
    { text: `${domain.Domain} has used ` },
    { text: `${formatCount(total)} of ${limit}`, figure: true },
    { text: ' today' },
  ]
  if (other === 0) return [...used, { text: ', all by this broadcast.' }]
  if (broadcast === 0) return [...used, { text: ', all by other emails.' }]
  return [
    ...used,
    { text: ': ' },
    { text: formatCount(broadcast), figure: true },
    { text: ' by this broadcast, ' },
    { text: formatCount(other), figure: true },
    { text: ' by other emails.' },
  ]
}

/** What the forecast takes for granted, with the move-up thresholds the API holds the domain to. */
export function forecastAssumption(domainName: string, limits: SendingDomainRateLimitsDto): string {
  return `This assumes ${domainName} keeps moving up (hard bounces ${formatLimit(limits.MaxHardBouncePercentToMoveUp)} or less `
    + `and complaints ${formatLimit(limits.MaxComplaintPercentToMoveUp)} or less over the last 3 days) and that emails to `
    + 'people who registered and broadcasts scheduled earlier leave room each day. If it holds at a step, this finishes later.'
}

/**
 * "its sending domain, webinars.ortec.com, stopped on the same bounces (over the domain's 3% limit for the
 * last 3 days), so nothing sends until a company admin resumes the domain" — "the same" when the domain
 * stopped on the rate that paused the broadcast.
 */
export function stoppedDomainClause(
  domainName: string,
  cause: DomainStopCause | null,
  tripMetric: BlastTripMetric | null,
  limits: SendingDomainRateLimitsDto,
): string {
  const subject = `its sending domain, ${domainName}, stopped`
  const until = 'so nothing sends until a company admin resumes the domain'
  const bounceLimit = formatLimit(limits.StopHardBouncePercent)
  const complaintLimit = formatLimit(limits.StopComplaintPercent)
  switch (cause) {
    case 'hardBounces':
      return `${subject} on ${tripMetric === 'HardBounce' ? 'the same bounces' : 'hard bounces'} `
        + `(over the domain's ${bounceLimit} limit for the last 3 days), ${until}`
    case 'complaints':
      return `${subject} on ${tripMetric === 'Complaint' ? 'the same complaints' : 'complaints'} `
        + `(over the domain's ${complaintLimit} limit for the last 3 days), ${until}`
    case 'both':
      return `${subject} on hard bounces and complaints `
        + `(over the domain's ${bounceLimit} and ${complaintLimit} limits for the last 3 days), ${until}`
    default:
      return `${subject}, ${until}`
  }
}

const BOUNCE_ADVICE =
  "Hard bounces this high usually mean stale addresses. If the lists this broadcast sends to haven't been " +
  'cleaned recently, cancel it and send again to a cleaned list.'

const COMPLAINT_ADVICE =
  "Complaints this high usually mean the audience didn't expect this email. Check the targeting and the " +
  'subject line before this broadcast sends again.'

/** "What to check first" while the domain is stopped: the stop's cause, then what resuming would send. */
export function stoppedDomainGuidance(cause: DomainStopCause | null, held: number): string | null {
  const advice = cause === 'complaints' ? COMPLAINT_ADVICE : cause ? BOUNCE_ADVICE : null
  const resuming = held > 0
    ? `Resuming sends the ${formatCount(held)} to the same addresses, and if the domain stops again it resumes one step lower again.`
    : null
  return [advice, resuming].filter(Boolean).join(' ') || null
}

/**
 * Beside a stopped domain's broadcast: why Resume is off, what the domain sends once a company admin resumes
 * it, and roughly how long the held emails then take.
 */
export function stoppedDomainResumeNote(plan: EmailBlastSendingDomainPlanDto, held: number, broadcastPaused: boolean): string | null {
  const domain = plan.Domain
  if (!domain) return null
  const resumeOff = `Resume is off while ${domain.Domain} is stopped.`
  if (domain.ResumeDailyLimit == null) {
    console.error('Stopped sending domain has no resume limit', domain.Id)
    return broadcastPaused ? resumeOff : null
  }

  const limit = formatCount(domain.ResumeDailyLimit)
  // A domain stopped on step 1 resumes on step 1; every other stop, a Ready one included, resumes one lower.
  const step = domain.WarmupStage != null && domain.ResumeStep === domain.WarmupStage
    ? 'at step 1, where it stopped'
    : 'one step below where it stopped'
  const resumes = broadcastPaused
    ? `${resumeOff} Once a company admin resumes the domain, it sends up to ${limit} a day, ${step}, and you can resume this broadcast.`
    : `Once a company admin resumes ${domain.Domain}, it sends up to ${limit} a day, ${step}, and this broadcast carries on.`

  const days = plan.DaysToSendAfterResume
  if (days == null || held <= 0) return resumes
  return `${resumes} Expect about ${limit} a day while ${stopWindowCause(domain.StoppedAtUtc, domainStopCause(domain))} `
    + `in the domain's 3-day window, so roughly ${formatCount(days)} ${days === 1 ? 'day' : 'days'} for the ${formatCount(held)}.`
}

// "Saturday's bounces are": the rates that stopped the domain, by the day it stopped in the viewer's time.
function stopWindowCause(stoppedAtUtc: string | null | undefined, cause: DomainStopCause | null): string {
  if (!cause) return 'the rates that stopped it are'
  const noun = cause === 'hardBounces' ? 'bounces' : cause === 'complaints' ? 'complaints' : 'bounces and complaints'
  return stoppedAtUtc ? `${localWeekday(stoppedAtUtc)}'s ${noun} are` : `the ${noun} that stopped it are`
}

/** "4 invitations were cancelled because they would have arrived after the event started." */
export function cancelledLateLine(count: number): string | null {
  if (count <= 0) return null
  return count === 1
    ? '1 invitation was cancelled because it would have arrived after the event started.'
    : `${formatCount(count)} invitations were cancelled because they would have arrived after the event started.`
}

/** A warm-up day as the verdict names it: always its UTC date, "Thursday 1 October". */
export function utcLongDate(dayStartUtc: string): string {
  const date = parseServerDate(dayStartUtc)
  if (!date) {
    console.error('Unparseable timestamp', dayStartUtc)
    return ''
  }
  return `${WEEKDAYS[date.getUTCDay()]} ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`
}

/** A warm-up day's weekday, by its UTC date: "Thursday". */
export function utcWeekday(dayStartUtc: string): string {
  const date = parseServerDate(dayStartUtc)
  if (!date) {
    console.error('Unparseable timestamp', dayStartUtc)
    return ''
  }
  return WEEKDAYS[date.getUTCDay()]
}

// An instant's weekday in the viewer's own time, to sit beside localTimeWithZone.
function localWeekday(utc: string): string {
  const date = parseServerDate(utc)
  if (!date) {
    console.error('Unparseable timestamp', utc)
    return ''
  }
  return WEEKDAYS[date.getDay()]
}
